import { FC } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';
import { CaretDownIcon, CaretUpIcon, MinusIcon } from '@phosphor-icons/react';

import { MissingDocsLegend } from './MissingDocsLegend';

interface StatsCardsProps {
  totalDomains: number;
  completedDomains: number;
  mandatoryDomains: number;
  optionalDomains: number;
  totalDocuments: number;
  totalMissingDocuments: number;
  /**
   * Re-assessment context. All four are `undefined` for v1 assessments;
   * when defined, the cards render with delta tags + colour-coding.
   */
  editedDomainCount?: number;
  parentTotalDocuments?: number;
  parentTotalMissing?: number;
  parentVersion?: number;
}

type DeltaDirection = 'up' | 'down' | 'flat';

/**
 * Pill showing the difference between the current value and the parent
 * version's value. `improvedDirection` decides which direction is "good":
 * for documents, more is better → up=green; for missing-docs, fewer is
 * better → down=green.
 */
const DeltaTag: FC<{
  delta: number;
  improvedDirection: 'up' | 'down';
}> = observer(({ delta, improvedDirection }) => {
  const direction: DeltaDirection = delta === 0 ? 'flat' : delta > 0 ? 'up' : 'down';
  const isImproving = direction === improvedDirection;
  const isFlat = direction === 'flat';

  const color = isFlat ? 'text.tertiary' : isImproving ? 'green.700' : 'error.main';
  const bg = isFlat ? 'grey.100' : isImproving ? 'green.100' : 'error.lighter';
  const borderColor = isFlat ? 'grey.300' : isImproving ? 'green.200' : 'error.light';

  const Icon = direction === 'up' ? CaretUpIcon : direction === 'down' ? CaretDownIcon : MinusIcon;
  const label = direction === 'flat' ? '0' : `${delta > 0 ? '+' : ''}${delta}`;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.375,
        px: 0.875,
        py: 0.25,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color,
        backgroundColor: bg,
        border: 1,
        borderColor,
        borderStyle: 'solid',
        lineHeight: 1.2,
      }}
    >
      <Icon size={10} weight="bold" />
      {label}
    </Box>
  );
});

export const StatsCards: FC<StatsCardsProps> = observer(
  ({
    totalDomains,
    completedDomains,
    mandatoryDomains,
    optionalDomains,
    totalDocuments,
    totalMissingDocuments,
    editedDomainCount,
    parentTotalDocuments,
    parentTotalMissing,
    parentVersion,
  }) => {
    const theme = useTheme();

    const isReassessment = editedDomainCount !== undefined;

    // Document delta vs the parent version. A positive delta means the user
    // has added more docs since the parent (which is "improving"); negative
    // means they have fewer.
    const docsDelta =
      parentTotalDocuments !== undefined ? totalDocuments - parentTotalDocuments : undefined;

    // Missing-doc delta. Negative is "improving" — fewer missing standard
    // documents than the parent. Positive is "worse".
    const missingDelta =
      parentTotalMissing !== undefined ? totalMissingDocuments - parentTotalMissing : undefined;

    // Re-colour the Missing Documents card by direction when re-assessing.
    const missingDirection: 'better' | 'worse' | 'flat' | 'plain' =
      missingDelta === undefined
        ? 'plain'
        : missingDelta < 0
          ? 'better'
          : missingDelta > 0
            ? 'worse'
            : 'flat';

    const missingCardSx = (() => {
      if (missingDirection === 'better') {
        return { borderColor: 'green.200', backgroundColor: 'green.100' } as const;
      }
      if (missingDirection === 'worse') {
        return { borderColor: 'error.light', backgroundColor: 'error.lighter' } as const;
      }
      return { borderColor: 'grey.400', backgroundColor: 'background.paper' } as const;
    })();

    const missingValueColor =
      missingDirection === 'better'
        ? 'green.700'
        : missingDirection === 'worse'
          ? 'error.main'
          : totalMissingDocuments > 0
            ? 'error.main'
            : 'text.primary';

    const missingLabelColor =
      missingDirection === 'better'
        ? 'green.700'
        : missingDirection === 'worse'
          ? 'error.main'
          : 'text.secondary';

    const cardBaseSx = {
      flex: 1,
      borderRadius: 2,
      border: `1px solid ${theme.palette.grey[400]}`,
      boxShadow: '0px 1px 0px rgba(72, 61, 61, 0.26)',
      p: 3.25,
    } as const;

    return (
      <Paper variant="panel" elevation={0} sx={{ border: `1px solid ${theme.palette.grey[300]}` }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
          {/* Total Domains Card */}
          <Paper elevation={1} variant="outlined" sx={cardBaseSx}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
              <Typography variant="textSM_regular">
                <Trans i18nKey="assessments.summary.totalDomains" />
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="displaySM_regular">{totalDomains}</Typography>
                <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                  <Trans i18nKey="assessments.summary.mandatory" values={{ mandatoryDomains, optionalDomains }} />
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* When re-assessing, surface "Domains Edited" instead of "Completion" — the design's
              second card emphasises the version-over-version delta rather than completion state. */}
          {isReassessment ? (
            <Paper elevation={1} variant="outlined" sx={cardBaseSx}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                <Typography variant="textSM_regular">
                  <Trans i18nKey="assessments.summary.domainsEdited" />
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="displaySM_regular">{editedDomainCount}</Typography>
                    <DeltaTag delta={editedDomainCount} improvedDirection="up" />
                  </Box>
                  <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                    <Trans
                      i18nKey="assessments.summary.domainsKept"
                      values={{ count: totalDomains - editedDomainCount }}
                    />
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ) : (
            <Paper elevation={1} variant="outlined" sx={cardBaseSx}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                <Typography variant="textSM_regular">
                  <Trans i18nKey="assessments.summary.completion" />
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="displaySM_regular" sx={{ color: 'green.700' }}>
                    {completedDomains}/{totalDomains}
                  </Typography>
                  <Typography variant="textSM_medium" sx={{ color: 'green.700' }}>
                    <Trans i18nKey="assessments.summary.allComplete" />
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Documents Card */}
          <Paper elevation={1} variant="outlined" sx={cardBaseSx}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
              <Typography variant="textSM_regular">
                <Trans i18nKey="assessments.summary.documents" />
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="displaySM_regular">{totalDocuments}</Typography>
                  {docsDelta !== undefined && (
                    <DeltaTag delta={docsDelta} improvedDirection="up" />
                  )}
                </Box>
                <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                  <Trans i18nKey="assessments.summary.evidenceUploaded" />
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Missing Documents Card — colour-coded by direction when re-assessing */}
          <Paper
            elevation={1}
            variant="outlined"
            sx={{ ...cardBaseSx, ...missingCardSx }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
              <Typography variant="textSM_regular" sx={{ color: missingLabelColor }}>
                <Trans i18nKey="assessments.summary.missingDocumentsCard" />
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="displaySM_regular" sx={{ color: missingValueColor }}>
                    {totalMissingDocuments}
                  </Typography>
                  {missingDelta !== undefined && (
                    <DeltaTag delta={missingDelta} improvedDirection="down" />
                  )}
                </Box>
                {missingDelta !== undefined ? (
                  <Typography variant="textXS_regular" sx={{ color: missingLabelColor }}>
                    <Trans
                      i18nKey={
                        missingDirection === 'better'
                          ? 'assessments.summary.missingDocumentsImproving'
                          : missingDirection === 'worse'
                            ? 'assessments.summary.missingDocumentsRegressing'
                            : 'assessments.summary.missingDocumentsUnchanged'
                      }
                      values={{ parentTotalMissing }}
                    />
                  </Typography>
                ) : (
                  <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                    <Trans i18nKey="assessments.summary.missingDocumentsCardSubtitle" />
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>

        {isReassessment && (
          <Box sx={{ mt: 1.5, mb: 1.5, display: 'flex', justifyContent: 'flex-start', px: 3.25 }}>
            <MissingDocsLegend parentVersion={parentVersion} />
          </Box>
        )}
      </Paper>
    );
  },
);
